/**
 * Broker Balance Utilities
 * Pure functions for parsing and computing balance metrics
 */

/**
 * Parse numeric value from various formats
 * @param {any} value - Value to parse
 * @returns {number|null} Parsed number or null
 */
export const parseNumericValue = (value) => {
  if (value === null || value === undefined) return null;

  if (typeof value === 'number') {
    if (Number.isNaN(value)) return null;
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // Remove spaces for safety
    let sanitized = trimmed.replace(/\s+/g, '');
    const hasComma = sanitized.includes(',');
    const hasDot = sanitized.includes('.');

    if (hasComma) {
      if (hasDot) {
        const lastComma = sanitized.lastIndexOf(',');
        const lastDot = sanitized.lastIndexOf('.');
        if (lastComma > lastDot) {
          // Treat dot as thousands separator and comma as decimal separator
          sanitized = sanitized.replace(/\./g, '');
          sanitized = sanitized.replace(/,/g, '.');
        } else {
          // Treat comma as thousands separator
          sanitized = sanitized.replace(/,/g, '');
        }
      } else {
        // Only comma present, treat as decimal separator
        sanitized = sanitized.replace(/,/g, '.');
      }
    }

    // Remove currency symbols or any leftover non-numeric characters
    sanitized = sanitized.replace(/[^0-9.+-]/g, '');

    const parsed = parseFloat(sanitized);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

/**
 * Get nested value from object using dot notation
 * @param {object} obj - Object to traverse
 * @param {string} path - Dot notation path
 * @returns {any} Value at path or undefined
 */
export const getNestedValue = (obj, path) => {
  if (!obj || typeof obj !== 'object') return undefined;

  const segments = path.split('.');
  let current = obj;

  for (const segment of segments) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = current[segment];
  }

  return current;
};

/**
 * Pick first non-null value from multiple paths
 * @param {object} entry - Object to search
 * @param {string[]} paths - Array of dot notation paths
 * @returns {any} First non-null value or null
 */
export const pickFirstValue = (entry, paths) => {
  if (!entry) return null;

  for (const path of paths) {
    const value = getNestedValue(entry, path);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  return null;
};

/**
 * Compute balance metrics from raw data
 * @param {object} entry - Raw balance entry
 * @returns {object|null} Computed balance metrics
 */
export const computeBalanceMetrics = (entry) => {
  if (!entry || typeof entry !== 'object') return null;

  const cash = parseNumericValue(pickFirstValue(entry, [
    'total_cash',
    'total-cash',
    'totalCash',
    'cash_balance',
    'cash-balance',
    'cashBalance',
    'cash',
    'cash_available',
    'available_cash',
    'availableCash',
    'balance.cash-balance',
    'balance.cash_balance',
    'raw_balances.total_cash',
    'raw_balances.total-cash',
    'raw_balances.cash_balance',
    'raw_balances.cash-balance'
  ]));

  const totalEquity = parseNumericValue(pickFirstValue(entry, [
    'total_equity',
    'totalEquity',
    'net_liquidating_value',
    'net-liquidating-value',
    'accountValue',
    'equity',
    'balance.net_liquidating_value',
    'balance.net-liquidating-value',
    'raw_balances.net_liquidating_value',
    'raw_balances.net-liquidating-value'
  ]));

  const buyingPower = parseNumericValue(pickFirstValue(entry, [
    'buying_power',
    'buyingPower',
    'total_buying_power',
    'total-buying-power',
    'available_trading_funds',
    'available-trading-funds',
    'derivative_buying_power',
    'derivative-buying-power',
    'balance.buying_power',
    'balance.buying-power',
    'raw_balances.total_buying_power',
    'raw_balances.total-buying-power',
    'raw_balances.derivative-buying-power',
    'raw_balances.equity-buying-power'
  ]));

  const optionBuyingPower = parseNumericValue(pickFirstValue(entry, [
    'option_buying_power',
    'optionBuyingPower',
    'option-buying-power',
    'day_trading_buying_power',
    'day-trading-buying-power',
    'raw_balances.option_buying_power',
    'raw_balances.option-buying-power'
  ]));

  return {
    cash: cash ?? 0,
    totalCash: cash ?? 0,
    totalEquity: totalEquity ?? 0,
    buyingPower: buyingPower ?? 0,
    optionBuyingPower: optionBuyingPower ?? 0,
    raw: entry
  };
};

/**
 * Get account identifiers from various formats
 * @param {object} item - Item containing account info
 * @returns {string[]} Array of account identifiers
 */
export const getAccountIdentifiers = (item) => {
  if (!item || typeof item !== 'object') return [];

  const identifiers = [
    item.accountNumber,
    item.account_number,
    item['account-number'],
    item.accountId,
    item.account_id,
    item['account-id'],
    item.id,
    item.profile_account_number,
    getNestedValue(item, 'account.accountNumber'),
    getNestedValue(item, 'raw_balances.account_number')
  ];

  // Check arrays
  if (Array.isArray(item.account)) {
    item.account.forEach(accountEntry => {
      identifiers.push(
        accountEntry?.accountNumber,
        accountEntry?.account_number,
        accountEntry?.['account-number'],
        accountEntry?.id
      );
    });
  }

  if (Array.isArray(item.accounts)) {
    item.accounts.forEach(accountEntry => {
      identifiers.push(
        accountEntry?.accountNumber,
        accountEntry?.account_number,
        accountEntry?.['account-number'],
        accountEntry?.id
      );
    });
  }

  return identifiers
    .map(identifier => (identifier !== undefined && identifier !== null ? String(identifier) : null))
    .filter(Boolean);
};

/**
 * Check if item matches account number
 * @param {object} item - Item to check
 * @param {string} accountNumber - Account number to match
 * @returns {boolean} True if matches
 */
export const matchesAccountNumber = (item, accountNumber) => {
  if (!accountNumber) return false;
  const identifiers = getAccountIdentifiers(item);
  return identifiers.some(identifier => identifier === String(accountNumber));
};

/**
 * Select balance entry from various sources
 * @param {any} source - Balance source (array or object)
 * @param {string} accountNumber - Optional account number to match
 * @returns {object|null} Selected balance entry
 */
export const selectBalanceEntry = (source, accountNumber) => {
  if (!source) return null;

  if (Array.isArray(source)) {
    if (source.length === 0) return null;
    if (accountNumber) {
      const match = source.find(entry => matchesAccountNumber(entry, accountNumber));
      if (match) return match;
    }
    return source[0];
  }

  if (typeof source === 'object') {
    if (Array.isArray(source.balances)) {
      const nested = selectBalanceEntry(source.balances, accountNumber);
      if (nested) return nested;
    }

    if (source.balance && typeof source.balance === 'object') {
      return { ...source, ...source.balance };
    }

    if (source.raw_balances && typeof source.raw_balances === 'object') {
      return { ...source, ...source.raw_balances };
    }

    return source;
  }

  return null;
};

/**
 * Resolve balance data from integration data
 * @param {object} integrationData - Raw integration data
 * @param {string} accountNumber - Account number to match
 * @returns {object} Balance metrics and raw data
 */
export const resolveBalanceData = (integrationData, accountNumber) => {
  if (!integrationData) {
    return { metrics: null, raw: null };
  }

  const candidateSources = [];

  // Check various locations for balance data
  if (integrationData.metadata?.balanceData) {
    candidateSources.push(integrationData.metadata.balanceData);
  }
  if (integrationData.metadata?.selectedAccountData?.balanceData) {
    candidateSources.push(integrationData.metadata.selectedAccountData.balanceData);
  }
  if (integrationData.metadata?.selectedAccountData) {
    candidateSources.push(integrationData.metadata.selectedAccountData);
  }
  if (integrationData.balances) {
    candidateSources.push(integrationData.balances);
  }
  if (integrationData.balance) {
    candidateSources.push(integrationData.balance);
  }
  if (integrationData.accountInfo?.balances) {
    candidateSources.push(integrationData.accountInfo.balances);
  }
  if (integrationData.accountInfo?.balance) {
    candidateSources.push(integrationData.accountInfo.balance);
  }
  if (integrationData.accountInfo) {
    candidateSources.push(integrationData.accountInfo);
  }
  if (integrationData.account) {
    candidateSources.push(integrationData.account);
  }
  if (integrationData.accounts) {
    candidateSources.push(integrationData.accounts);
  }
  if (integrationData.profile?.balance) {
    candidateSources.push(integrationData.profile.balance);
  }

  let fallbackRaw = null;

  for (const source of candidateSources) {
    const entry = selectBalanceEntry(source, accountNumber);
    if (!entry) continue;

    if (!fallbackRaw) {
      fallbackRaw = entry;
    }

    const metrics = computeBalanceMetrics(entry);
    if (metrics) {
      return { metrics, raw: entry };
    }
  }

  return { metrics: null, raw: fallbackRaw };
};

/**
 * Build account data from balance metrics
 * @param {object} param - Balance metrics and raw data
 * @returns {object} Formatted account data
 */
export const buildAccountDataFromMetrics = ({ metrics, raw }) => {
  if (!metrics) {
    return {
      balance: 0,
      buying_power: 0,
      total_equity: 0,
      total_cash: 0,
      option_buying_power: 0,
      rawBalance: raw || null
    };
  }

  return {
    balance: metrics.cash ?? 0,
    buying_power: metrics.buyingPower ?? 0,
    total_equity: metrics.totalEquity ?? 0,
    total_cash: metrics.totalCash ?? metrics.cash ?? 0,
    option_buying_power: metrics.optionBuyingPower ?? 0,
    rawBalance: raw || metrics.raw || null
  };
};